import { useEffect, useState } from "react";
import {
  Check,
  Copy,
  Loader2,
  RefreshCw,
  Shield,
  Trash2,
  User,
} from "lucide-react";
import { api } from "../../api";
import { reportSwallowed } from "../../lib/reportSwallowed";
import { useOrgStore } from "../../stores/orgStore";
import { useAuthStore } from "../../stores/authStore";
import { joinOrgByCode, createOrgAction } from "./orgActions";
import {
  DEFAULT_SOIL_UNITS,
  K_DISPLAY_OPTIONS,
  MASS_BASIS_OPTIONS,
  MAX_DISPLAY_DEPTH_IN,
  MIN_DISPLAY_DEPTH_IN,
  P_DISPLAY_OPTIONS,
  parseSoilUnitsWire,
  type SoilUnitPrefs,
} from "../../lib/soilUnits";
import type { OrgMember, OrgMembership } from "../../types/contract";
import { Button } from "../../ui/Button";
import { DialogHeader, Popup } from "../../ui/Popup";

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : "Something went wrong.";
}
function copy(text: string) {
  // writeText is async — chain.catch so a denied/unfocused clipboard doesn't become an unhandled
  // rejection (the ?. guards only the sync missing-clipboard case). Fire-and-forget by design.
  try {
    void navigator.clipboard
      ?.writeText(text)
      .catch((err) => reportSwallowed("clipboard write", err));
  } catch {
    /* no clipboard here */
  }
}

function Shell({
  title,
  onClose,
  children,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <Popup
      onClose={onClose}
      className={`flex max-h-[85vh] w-full ${wide ? "max-w-md" : "max-w-sm"} flex-col rounded-lg bg-surface text-fg shadow-2xl`}
    >
      <DialogHeader title={title} onClose={onClose} />
      <div className="overflow-auto p-4">{children}</div>
    </Popup>
  );
}

const codeBox =
  "flex items-center justify-between gap-2 rounded border border-line bg-canvas px-2 py-1.5 font-mono text-[13px]";

export function LinkOrgModal({ onClose }: { onClose: () => void }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function go() {
    if (!code.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await joinOrgByCode(code.trim());
      onClose();
    } catch (e) {
      setErr(errMsg(e));
      setBusy(false);
    }
  }
  return (
    <Shell title="Link an organization" onClose={onClose}>
      <p className="mb-2 text-[12px] text-muted">
        Paste the organization's join code. You'll be added as a member and
        switched into it.
      </p>
      <input
        autoFocus
        value={code}
        onChange={(e) => setCode(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void go();
        }}
        placeholder="Join code"
        aria-label="Join code"
        className="mb-2 w-full rounded border border-line bg-canvas px-2 py-1.5 font-mono text-[13px] text-fg outline-none focus:border-accent"
      />
      {err && <p className="mb-2 text-[11px] text-red-400">{err}</p>}
      <Button disabled={busy || !code.trim()} onClick={() => void go()}>
        {busy && <Loader2 size={12} className="animate-spin" />} Link
        organization
      </Button>
    </Shell>
  );
}

export function CreateOrgModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [created, setCreated] = useState<OrgMembership | null>(null);
  async function go() {
    if (!name.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      setCreated(await createOrgAction(name.trim()));
      setBusy(false);
    } catch (e) {
      setErr(errMsg(e));
      setBusy(false);
    }
  }
  return (
    <Shell title="Create an organization" onClose={onClose}>
      {!created ? (
        <>
          <p className="mb-2 text-[12px] text-muted">
            Name your organization. You'll be its admin and get a join code to
            share with others.
          </p>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void go();
            }}
            placeholder="Organization name"
            aria-label="Organization name"
            className="mb-2 w-full rounded border border-line bg-canvas px-2 py-1.5 text-[13px] text-fg outline-none focus:border-accent"
          />
          {err && <p className="mb-2 text-[11px] text-red-400">{err}</p>}
          <Button disabled={busy || !name.trim()} onClick={() => void go()}>
            {busy && <Loader2 size={12} className="animate-spin" />} Create
            organization
          </Button>
        </>
      ) : (
        <>
          <p className="mb-2 flex items-center gap-1.5 text-[12px] text-emerald-300">
            <Check size={14} /> “{created.name}” created — you're now working in
            it.
          </p>
          <p className="mb-1 text-[11px] text-muted">
            Share this join code so others can join:
          </p>
          <div className={`${codeBox} mb-3`}>
            <span className="truncate text-fg">{created.join_code}</span>
            <button
              className="shrink-0 rounded p-1 text-muted hover:bg-hover hover:text-fg"
              aria-label="Copy join code"
              onClick={() => created.join_code && copy(created.join_code)}
            >
              <Copy size={13} />
            </button>
          </div>
          <Button onClick={onClose}>Done</Button>
        </>
      )}
    </Shell>
  );
}

// Manage an org you administer: join code + rotate, rename, members (remove / change role).
export function ManageOrgModal({
  org,
  onClose,
}: {
  org: OrgMembership;
  onClose: () => void;
}) {
  const refresh = useOrgStore((s) => s.refresh);
  const currentUserId = useAuthStore((s) => s.session?.user_id);
  const [code, setCode] = useState(org.join_code ?? "");
  const [name, setName] = useState(org.name);
  const [rotating, setRotating] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [members, setMembers] = useState<OrgMember[] | null>(null);
  const [membersErr, setMembersErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .listOrgMembers(org.org_id)
      .then((m) => {
        if (live) setMembers(m);
      })
      .catch((e) => {
        if (live) setMembersErr(errMsg(e));
      });
    return () => {
      live = false;
    };
  }, [org.org_id]);

  async function rotate() {
    setRotating(true);
    setNote(null);
    try {
      const r = await api.rotateJoinCode(org.org_id);
      setCode(r.join_code);
      void refresh();
      setNote(
        "Join code rotated — the old one no longer works. Existing members keep access.",
      );
    } catch (e) {
      setNote(errMsg(e));
    } finally {
      setRotating(false);
    }
  }
  // DEFAULT SOIL UNITS: the org-wide default display lens for members who have never chosen their
  // own.
  const [units, setUnits] = useState<SoilUnitPrefs | null>(null);
  const [unitsLoaded, setUnitsLoaded] = useState(false);
  const [savingUnits, setSavingUnits] = useState(false);
  const [unitsNote, setUnitsNote] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    api
      .fetchOrgSoilUnits(org.org_id)
      .then((r) => {
        if (live) {
          setUnits(parseSoilUnitsWire(r));
          setUnitsLoaded(true);
        }
      })
      .catch(() => {
        if (live) {
          setUnitsLoaded(true);
          setUnitsNote(
            "Org defaults aren’t on the server yet — saving will fail until the update lands.",
          );
        }
      });
    return () => {
      live = false;
    };
  }, [org.org_id]);
  async function saveUnits(next: SoilUnitPrefs | null) {
    setSavingUnits(true);
    setUnitsNote(null);
    try {
      await api.updateOrgSoilUnits(
        org.org_id,
        next == null
          ? null
          : {
              depth_in: next.depthIn,
              p_extractant: next.pExtractant,
              k_extractant: next.kExtractant,
              mass_basis: next.massBasis,
            },
      );
      setUnits(next);
      setUnitsNote(
        next == null
          ? "Default cleared — new members get the system defaults."
          : "Saved — members who haven’t chosen their own units will read through these.",
      );
    } catch (e) {
      setUnitsNote(errMsg(e));
    } finally {
      setSavingUnits(false);
    }
  }

  async function saveName() {
    if (!name.trim() || name.trim() === org.name) return;
    setSavingName(true);
    try {
      await api.renameOrg(org.org_id, name.trim());
      void refresh();
    } catch (e) {
      setNote(errMsg(e));
    } finally {
      setSavingName(false);
    }
  }
  const adminCount = (members ?? []).filter(
    (m) => m.role === "org_admin",
  ).length;
  const isSoleAdmin = (m: OrgMember) =>
    m.role === "org_admin" && adminCount <= 1;

  async function removeMember(m: OrgMember) {
    if (isSoleAdmin(m)) return; // never orphan the org
    try {
      await api.removeOrgMember(org.org_id, m.user_id);
      if (m.user_id === currentUserId) {
        const session = useAuthStore.getState().session;
        if (session) {
          const orgs = session.orgs.filter((item) => item.id !== org.org_id);
          useAuthStore.setState({
            session: {
              ...session,
              orgs,
              role: orgs[0]?.role ?? session.role,
            },
          });
        }
      }
      setMembers((cur) => (cur ?? []).filter((x) => x.user_id !== m.user_id));
    } catch (e) {
      setNote(errMsg(e));
    }
  }
  async function toggleRole(m: OrgMember) {
    const role = m.role === "org_admin" ? "org_member" : "org_admin";
    if (role === "org_member" && isSoleAdmin(m)) {
      setNote("This is the only admin — promote someone else first.");
      return;
    }
    try {
      const r = await api.setOrgMemberRole(org.org_id, m.user_id, role);
      // the response carries only {user_id, role} — update the role in place, keeping email/name
      setMembers((cur) =>
        (cur ?? []).map((x) =>
          x.user_id === m.user_id ? { ...x, role: r.role } : x,
        ),
      );
      if (m.user_id === currentUserId) {
        const session = useAuthStore.getState().session;
        if (session) {
          useAuthStore.setState({
            session: {
              ...session,
              orgs: session.orgs.map((item) =>
                item.id === org.org_id ? { ...item, role } : item,
              ),
            },
          });
        }
      }
      void refresh();
      // demoting YOURSELF ends your admin rights — the manage panel's actions would now 403, so close it
      if (m.user_id === currentUserId && role === "org_member") onClose();
    } catch (e) {
      setNote(errMsg(e));
    }
  }

  return (
    <Shell title={`Manage ${org.name}`} onClose={onClose} wide>
      <div className="flex flex-col gap-4 text-[12px]">
        <div>
          <div className="mb-1 font-medium text-muted">Join code</div>
          <div className={codeBox}>
            <span className="truncate text-fg">{code}</span>
            <div className="flex shrink-0 items-center gap-1">
              <button
                className="rounded p-1 text-muted hover:bg-hover hover:text-fg"
                aria-label="Copy join code"
                onClick={() => copy(code)}
              >
                <Copy size={13} />
              </button>
              <button
                className="flex items-center gap-1 rounded px-1.5 py-1 text-muted hover:bg-hover hover:text-fg disabled:opacity-50"
                disabled={rotating}
                onClick={() => void rotate()}
              >
                {rotating ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <RefreshCw size={12} />
                )}{" "}
                Rotate
              </button>
            </div>
          </div>
          <p className="mt-1 text-[10px] text-faint">
            Rotating changes the code for new joiners only — current members are
            never removed.
          </p>
        </div>

        <div>
          <div className="mb-1 font-medium text-muted">Name</div>
          <div className="flex items-center gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-label="Organization name"
              className="flex-1 rounded border border-line bg-canvas px-2 py-1 text-[13px] text-fg outline-none focus:border-accent"
            />
            <button
              className="rounded border border-line px-2 py-1 text-fg hover:bg-hover disabled:opacity-50"
              disabled={savingName || !name.trim() || name.trim() === org.name}
              onClick={() => void saveName()}
            >
              {savingName ? (
                <Loader2 size={12} className="inline animate-spin" />
              ) : (
                "Save"
              )}
            </button>
          </div>
        </div>

        <div>
          <div className="mb-1 font-medium text-muted">
            Default soil data units
          </div>
          <p className="mb-1.5 text-[10px] text-faint">
            The display units members start with. Applies to members who have
            never chosen their own units — anyone’s personal choice always wins
            over this default.
          </p>
          {!unitsLoaded ? (
            <p className="text-[11px] text-faint">Loading…</p>
          ) : (
            <div className="flex flex-col gap-1.5" data-testid="org-soil-units">
              {units == null ? (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-faint">
                    Not set — new members get the system defaults (8″ Mehlich-3,
                    ppm).
                  </span>
                  <button
                    className="rounded border border-line px-2 py-1 text-fg hover:bg-hover disabled:opacity-50"
                    disabled={savingUnits}
                    onClick={() => setUnits({ ...DEFAULT_SOIL_UNITS })}
                  >
                    Set a default
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-1">
                      <span className="text-[10px] text-muted">Depth</span>
                      <input
                        type="number"
                        step="0.5"
                        min={MIN_DISPLAY_DEPTH_IN}
                        max={MAX_DISPLAY_DEPTH_IN}
                        value={units.depthIn}
                        aria-label="Org default sample depth in inches"
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          if (
                            Number.isFinite(v) &&
                            v >= MIN_DISPLAY_DEPTH_IN &&
                            v <= MAX_DISPLAY_DEPTH_IN
                          )
                            setUnits({ ...units, depthIn: v });
                        }}
                        className="w-16 rounded border border-line bg-canvas px-1.5 py-1 text-[11px] text-fg outline-none focus:border-accent"
                      />
                      <span className="text-[10px] text-faint">″</span>
                    </label>
                    <label className="flex items-center gap-1">
                      <span className="text-[10px] text-muted">P</span>
                      <select
                        value={units.pExtractant}
                        aria-label="Org default phosphorus extractant"
                        onChange={(e) =>
                          setUnits({
                            ...units,
                            pExtractant: e.target
                              .value as SoilUnitPrefs["pExtractant"],
                          })
                        }
                        className="rounded border border-line bg-canvas px-1 py-1 text-[11px] text-fg"
                      >
                        {P_DISPLAY_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="flex items-center gap-1">
                      <span className="text-[10px] text-muted">K/micros</span>
                      <select
                        value={units.kExtractant}
                        aria-label="Org default potassium extractant"
                        onChange={(e) =>
                          setUnits({
                            ...units,
                            kExtractant: e.target
                              .value as SoilUnitPrefs["kExtractant"],
                          })
                        }
                        className="rounded border border-line bg-canvas px-1 py-1 text-[11px] text-fg"
                      >
                        {K_DISPLAY_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="flex items-center gap-1">
                      <span className="text-[10px] text-muted">Units</span>
                      <select
                        value={units.massBasis}
                        aria-label="Org default mass basis"
                        onChange={(e) =>
                          setUnits({
                            ...units,
                            massBasis: e.target
                              .value as SoilUnitPrefs["massBasis"],
                          })
                        }
                        className="rounded border border-line bg-canvas px-1 py-1 text-[11px] text-fg"
                      >
                        {MASS_BASIS_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="rounded border border-line px-2 py-1 text-fg hover:bg-hover disabled:opacity-50"
                      disabled={savingUnits}
                      onClick={() => void saveUnits(units)}
                    >
                      {savingUnits ? (
                        <Loader2 size={12} className="inline animate-spin" />
                      ) : (
                        "Save default"
                      )}
                    </button>
                    <button
                      className="rounded px-2 py-1 text-[11px] text-muted hover:bg-hover hover:text-fg disabled:opacity-50"
                      disabled={savingUnits}
                      onClick={() => void saveUnits(null)}
                    >
                      Clear (system defaults)
                    </button>
                  </div>
                </>
              )}
              {unitsNote && (
                <p className="text-[10px] text-muted">{unitsNote}</p>
              )}
            </div>
          )}
        </div>

        <div>
          <div className="mb-1 font-medium text-muted">Members</div>
          {membersErr && (
            <p className="text-[11px] text-red-400">{membersErr}</p>
          )}
          {!members && !membersErr && (
            <p className="text-[11px] text-faint">Loading members…</p>
          )}
          <ul className="flex flex-col gap-1">
            {members?.map((m) => (
              <li
                key={m.user_id}
                className="flex items-center justify-between gap-2 rounded border border-line/60 px-2 py-1.5"
              >
                <span className="flex min-w-0 items-center gap-1.5">
                  {m.role === "org_admin" ? (
                    <Shield size={12} className="shrink-0 text-accent" />
                  ) : (
                    <User size={12} className="shrink-0 text-faint" />
                  )}
                  <span className="truncate text-fg">{m.name ?? m.email}</span>
                  <span className="shrink-0 text-[10px] text-faint">
                    {m.role === "org_admin" ? "admin" : "member"}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-1">
                  <button
                    disabled={isSoleAdmin(m)}
                    title={
                      isSoleAdmin(m)
                        ? "The only admin — promote someone else first"
                        : undefined
                    }
                    className="rounded px-1.5 py-0.5 text-[10px] text-muted hover:bg-hover hover:text-fg disabled:opacity-40 disabled:hover:bg-transparent"
                    onClick={() => void toggleRole(m)}
                  >
                    {m.role === "org_admin" ? "Make member" : "Make admin"}
                  </button>
                  {m.user_id !== currentUserId && !isSoleAdmin(m) && (
                    <button
                      className="rounded p-1 text-faint hover:text-red-400"
                      aria-label={`Remove ${m.email}`}
                      onClick={() => void removeMember(m)}
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {note && <p className="text-[11px] text-muted">{note}</p>}
      </div>
    </Shell>
  );
}
